import { useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import Box from '@mui/material/Box';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import LinkOffRoundedIcon from '@mui/icons-material/LinkOffRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';

import { DrawerV2, ButtonV2, dsV2 } from '@bloomays-lib/ui.shared';
import { GET_SOCIETY_MISSIONS } from '@bloomays-lib/adapter.api-bloomer';
import { internationalPhoneRegex, mailRegex, nameRegex } from '@bloomays-lib/utils.shared';
import type { IContact } from '@bloomays-lib/types.shared';

import { FieldV2, InputV2, Row2, InfoV2, DividerV2, SectionHeading } from '../formFields';
import { SrcBadge } from '../components';
import type { ContactSlot } from '../../../../helpers/missionDashboardV2';
import { useContactMutations } from './useContactMutations';

export type ContactSource = 'mission' | 'society';

export type ContactEditDrawerProps = {
  open: boolean;
  onClose: () => void;
  contact: IContact;
  slot: ContactSlot;
  /** Niveau d'où provient le contact résolu (mission prioritaire / société repli). */
  source: ContactSource;
  missionId: string;
  societyId?: string;
  societyName?: string;
  onDone?: () => void | Promise<unknown>;
  /** Ouvre la fiche société correspondante (repli). */
  onOpenSociety?: () => void;
};

const NAME_ERROR = 'Min 1 lettre, sans emoji ni caractère spécial';
const MAIL_ERROR = 'Format requis : xxxx@xx.xx';
const PHONE_ERROR = 'Format requis : 0xxxxxxxxx ou +xxxxxxxxxxx';

type SocietyMissionsData = {
  society?: { recordId: string; name?: string; mission?: { recordId: string; label?: string; isActive?: boolean }[] }[];
};

/**
 * Édition d'un contact (§8) via `updateContact` + déliaison de l'emplacement. Au niveau société
 * (= repli global), la déliaison demande une confirmation listant les missions impactées
 * (décision spec #5, via `GET_SOCIETY_MISSIONS`). Le niveau (mission / société) est affiché en
 * lecture seule via le badge de source.
 */
export const ContactEditDrawer = ({
  open,
  onClose,
  contact,
  slot,
  source,
  missionId,
  societyId,
  societyName,
  onDone,
  onOpenSociety,
}: ContactEditDrawerProps) => {
  const mutations = useContactMutations({ missionId, onDone });

  const [firstname, setFirstname] = useState(contact.firstname ?? '');
  const [lastname, setLastname] = useState(contact.lastname ?? '');
  const [email, setEmail] = useState(contact.email ?? '');
  const [phoneNumber, setPhoneNumber] = useState(contact.phoneNumber ?? '');
  const [nameJob, setNameJob] = useState(contact.nameJob ?? '');
  const [confirming, setConfirming] = useState(false);

  const [fetchSocietyMissions, { data: societyData, loading: loadingMissions }] =
    useLazyQuery<SocietyMissionsData>(GET_SOCIETY_MISSIONS);

  const trimmed = {
    firstname: firstname.trim(),
    lastname: lastname.trim(),
    email: email.trim(),
    phoneNumber: phoneNumber.trim(),
    nameJob: nameJob.trim(),
  };
  const errors = {
    firstname: trimmed.firstname.length < 1 || !nameRegex.test(trimmed.firstname) ? NAME_ERROR : undefined,
    lastname: trimmed.lastname.length < 1 || !nameRegex.test(trimmed.lastname) ? NAME_ERROR : undefined,
    email: trimmed.email.length < 1 || !mailRegex.test(trimmed.email) ? MAIL_ERROR : undefined,
    phoneNumber: trimmed.phoneNumber && !internationalPhoneRegex.test(trimmed.phoneNumber) ? PHONE_ERROR : undefined,
  };
  const hasError = Object.values(errors).some(Boolean);
  const contactId = contact.recordId ?? '';

  const save = async () => {
    if (hasError || !contactId) return;
    const ok = await mutations.updateContact({
      recordId: contactId,
      firstname: trimmed.firstname,
      lastname: trimmed.lastname,
      email: trimmed.email,
      phoneNumber: trimmed.phoneNumber || undefined,
      nameJob: trimmed.nameJob || undefined,
    });
    if (ok) onClose();
  };

  const startUnlink = () => {
    if (source === 'society' && societyId) void fetchSocietyMissions({ variables: { id: societyId } });
    setConfirming(true);
  };

  const confirmUnlink = async () => {
    if (!contactId) return;
    const ok =
      source === 'society' && societyId && slot.societyRole
        ? await mutations.unlinkFromSociety(contactId, societyId, slot.societyRole)
        : await mutations.unlinkFromMission(contactId, slot.missionRole, slot.side);
    if (ok) onClose();
  };

  const impactedMissions = societyData?.society?.[0]?.mission ?? [];

  return (
    <DrawerV2
      open={open}
      onClose={onClose}
      title="Modifier le contact"
      subtitle={`${slot.side === 'supplier' ? 'Fournisseur' : 'Client'} · ${slot.label}`}
      width={480}
      footer={
        <>
          <ButtonV2 variant="secondary" onClick={onClose}>
            Annuler
          </ButtonV2>
          <ButtonV2
            onClick={save}
            disabled={hasError || mutations.busy}
            loading={mutations.busy}
            startIcon={<SaveRoundedIcon sx={{ fontSize: 15 }} />}
          >
            Enregistrer
          </ButtonV2>
        </>
      }
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
        <Box
          sx={{
            fontSize: 11,
            fontWeight: 700,
            color: dsV2.color.t3,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          Source
        </Box>
        <SrcBadge source={source} />
      </Box>

      <Row2>
        <FieldV2 label="Prénom" required error={errors.firstname}>
          <InputV2 value={firstname} onChange={setFirstname} placeholder="Prénom" />
        </FieldV2>
        <FieldV2 label="Nom" required error={errors.lastname}>
          <InputV2 value={lastname} onChange={setLastname} placeholder="Nom" />
        </FieldV2>
      </Row2>
      <FieldV2 label="Email" required error={errors.email}>
        <InputV2 type="email" value={email} onChange={setEmail} placeholder="prenom.nom@exemple.com" />
      </FieldV2>
      <FieldV2 label="Téléphone" error={errors.phoneNumber}>
        <InputV2 type="tel" value={phoneNumber} onChange={setPhoneNumber} placeholder="Optionnel" />
      </FieldV2>
      <FieldV2 label="Fonction">
        <InputV2 value={nameJob} onChange={setNameJob} placeholder="Optionnel" />
      </FieldV2>

      <DividerV2 />
      <SectionHeading>Liaison</SectionHeading>

      {!confirming ? (
        <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <ButtonV2
            variant="secondary"
            onClick={startUnlink}
            disabled={mutations.busy}
            startIcon={<LinkOffRoundedIcon sx={{ fontSize: 15 }} />}
          >
            Délier ce contact
          </ButtonV2>
          {source === 'society' && onOpenSociety ? (
            <ButtonV2
              variant="secondary"
              onClick={onOpenSociety}
              startIcon={<OpenInNewRoundedIcon sx={{ fontSize: 15 }} />}
            >
              Ouvrir la fiche société
            </ButtonV2>
          ) : null}
        </Box>
      ) : (
        <Box>
          {source === 'society' ? (
            <>
              <InfoV2 tone="amber">
                Ce contact est rattaché au niveau société{societyName ? ` « ${societyName} »` : ''}. Le délier le
                retirera du repli de <strong>toutes les missions</strong> de cette société.
              </InfoV2>
              <Box sx={{ fontSize: 11, fontWeight: 700, color: dsV2.color.t3, marginBottom: '6px' }}>
                MISSIONS IMPACTÉES
              </Box>
              {loadingMissions ? (
                <Box sx={{ fontSize: 12, color: dsV2.color.t4, paddingBottom: '10px' }}>Chargement…</Box>
              ) : impactedMissions.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '12px' }}>
                  {impactedMissions.map((mission) => (
                    <Box
                      key={mission.recordId}
                      sx={{
                        fontSize: '12px',
                        color: dsV2.color.noir,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          width: 5,
                          height: 5,
                          borderRadius: '50%',
                          background: mission.isActive ? dsV2.color.vert : dsV2.color.t4,
                        }}
                      />
                      {mission.label || mission.recordId}
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={{ fontSize: 12, color: dsV2.color.t4, fontStyle: 'italic', marginBottom: '12px' }}>
                  Aucune autre mission rattachée à cette société.
                </Box>
              )}
            </>
          ) : (
            <InfoV2 tone="amber">Délier ce contact de la mission ? Il restera disponible dans l'annuaire.</InfoV2>
          )}
          <Box sx={{ display: 'flex', gap: '8px' }}>
            <ButtonV2 variant="secondary" onClick={() => setConfirming(false)} disabled={mutations.busy}>
              Annuler
            </ButtonV2>
            <ButtonV2
              variant="danger"
              onClick={confirmUnlink}
              loading={mutations.busy}
              startIcon={<LinkOffRoundedIcon sx={{ fontSize: 15 }} />}
            >
              Confirmer le déliage
            </ButtonV2>
          </Box>
        </Box>
      )}
    </DrawerV2>
  );
};

export default ContactEditDrawer;
